import React, { useCallback, useEffect, useState } from 'react';

/**
 * ChaosPage — UI for the proxy's chaos/latency injection.
 *
 * Mirrors IuRewritePage.jsx's editing idiom (rules table, enabled toggles,
 * load/save via proxy REST) but talks to a plain request/response REST pair
 * instead of a WebSocket control channel:
 *
 *   GET  /proxy/chaos          -> { rules }
 *   POST /proxy/chaos { rules } -> { ok, rules, rejected }
 *
 * Rule shape: { id, enabled, match: {requestType?, host?, urlContains?},
 *   action: 'delay'|'block'|'mock'|'patch'|'swap', delayMs?, mock?: {status, contentType, body},
 *   patch?: "path = value" lines (proxy-server/jsonResponsePatch.js),
 *   swap?: "production_id = test_id" lines (proxy-server/responseIdSwap.js), note? }
 */

// The fallback is the HOST, not the path prefix — every call below adds its
// own `/proxy`. Folding the prefix into the fallback (…|| '/proxy') is what
// broke this in prod: it read correctly as `/proxy/chaos` locally, then the
// env var replaced the whole thing with the host and the prefix vanished,
// leaving https://proxy-api.adlensfx.com/chaos -> 404. Dev-only correctness.
const PROXY_BASE = (process.env.REACT_APP_PROXY_API_URL || '').replace(/\/$/, '');

const COMMON_REQUEST_TYPES = ['google', 'prebid', 'amazon', 'ima', 'sdk_bidding', 'analytics'];

const ACTIONS = [
  { value: 'delay', label: 'Delay' },
  { value: 'block', label: 'Block' },
  { value: 'mock', label: 'Mock response' },
  { value: 'patch', label: 'Patch JSON response' },
  { value: 'swap', label: 'Swap IDs in response' },
];

function newId() {
  return `chaos_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function emptyRule(overrides = {}) {
  return {
    id: newId(),
    enabled: false,
    match: { requestType: '', host: '', urlContains: '' },
    action: 'delay',
    delayMs: 2000,
    mock: { status: 200, contentType: 'application/json', body: '{}' },
    patch: '',
    swap: '',
    note: '',
    ...overrides,
  };
}

const PRESETS = [
  {
    label: 'GAM slow 5s',
    make: () => emptyRule({
      match: { requestType: 'google', host: '', urlContains: '' },
      action: 'delay',
      delayMs: 5000,
      note: 'GAM slow 5s',
    }),
  },
  {
    label: 'GAM outage',
    make: () => emptyRule({
      match: { requestType: 'google', host: '', urlContains: '' },
      action: 'block',
      note: 'GAM outage',
    }),
  },
  {
    label: 'Prebid empty body',
    make: () => emptyRule({
      match: { requestType: 'prebid', host: '', urlContains: '' },
      action: 'mock',
      mock: { status: 200, contentType: 'application/json', body: '{}' },
      note: 'Prebid empty body',
    }),
  },
  {
    label: 'IMA 404',
    make: () => emptyRule({
      match: { requestType: 'ima', host: '', urlContains: '' },
      action: 'mock',
      mock: { status: 404, contentType: 'text/plain', body: 'Not Found' },
      note: 'IMA 404',
    }),
  },
  {
    // Debug builds whose Ad Manager mediation config points at production apps:
    // pubsetting (adapter init) and every GAM ad response carry the IDs.
    label: 'Mediation test IDs',
    make: () => emptyRule({
      match: { requestType: '', host: 'doubleclick.net', urlContains: '' },
      action: 'swap',
      swap: '# Meta: full placement IDs (<app>_<placement>)\n# 114351662587_10167188220642588 = <test app>_<test placement>\n# Liftoff: app ID and each placement reference ID\n# <prod app id> = <test app id>',
      note: 'Mediation test IDs (Meta / Liftoff)',
    }),
  },
];

export default function ChaosPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [rejected, setRejected] = useState([]);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${PROXY_BASE}/proxy/chaos`)
      .then((r) => r.json())
      .then((data) => {
        setRules(Array.isArray(data?.rules) ? data.rules.map((r) => ({ ...emptyRule(), ...r })) : []);
      })
      .catch((e) => setError(e?.message || 'Could not load chaos rules'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(() => {
    setSaving(true);
    setError(null);
    setRejected([]);
    setSavedAt(null);
    fetch(`${PROXY_BASE}/proxy/chaos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rules }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data?.rules)) setRules(data.rules.map((r) => ({ ...emptyRule(), ...r })));
        if (Array.isArray(data?.rejected) && data.rejected.length) setRejected(data.rejected);
        setSavedAt(Date.now());
      })
      .catch((e) => setError(e?.message || 'Could not save chaos rules'))
      .finally(() => setSaving(false));
  }, [rules]);

  const addRule = () => setRules((rs) => [...rs, emptyRule()]);
  const addPreset = (preset) => setRules((rs) => [...rs, preset.make()]);
  const deleteRule = (id) => {
    if (window.confirm('Delete this chaos rule?')) {
      setRules((rs) => rs.filter((r) => r.id !== id));
    }
  };
  const updateRule = (id, patch) => setRules((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const updateMatch = (id, patch) => setRules((rs) => rs.map((r) => (r.id === id ? { ...r, match: { ...r.match, ...patch } } : r)));
  const updateMock = (id, patch) => setRules((rs) => rs.map((r) => (r.id === id ? { ...r, mock: { ...(r.mock || {}), ...patch } } : r)));

  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: 'var(--ink)' }}>🌪️ Chaos</h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--muted)', maxWidth: 640 }}>
            Inject latency, outages, or mocked responses into live ad traffic to test client
            timeout handling and failure recovery — delay a phase, block a request type, or
            swap in a canned response.
          </p>
        </div>
        {enabledCount > 0 && (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 10, whiteSpace: 'nowrap',
            color: '#ef4444', background: '#ef444422',
          }}>
            ● {enabledCount} rule{enabledCount === 1 ? '' : 's'} active
          </span>
        )}
      </div>

      {/* Warning banner */}
      <div style={{
        padding: '12px 16px', margin: '16px 0', borderRadius: 10,
        background: '#ef444414', border: '1px solid #ef444466',
        fontSize: 13, fontWeight: 600, color: 'var(--ink)',
      }}>
        ⚠️ Chaos rules affect ALL intercepted traffic while enabled — disable rules after testing.
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, borderRadius: 8,
          background: '#ef444414', border: '1px solid #ef444466', color: '#ef4444', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {rejected.length > 0 && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, borderRadius: 8,
          background: '#ef444414', border: '1px solid #ef444466', color: '#ef4444', fontSize: 12,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Some rules were rejected:</div>
          {rejected.map((r, i) => (
            <div key={i} style={{ fontFamily: 'monospace' }}>
              {typeof r === 'string' ? r : JSON.stringify(r)}
            </div>
          ))}
        </div>
      )}

      {/* Presets */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>Quick presets</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PRESETS.map((p) => (
            <button key={p.label} onClick={() => addPreset(p)} style={ghostBtn}>
              + {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={addRule} style={primaryBtn}>+ Add rule</button>
        <button onClick={save} disabled={saving} style={{ ...primaryBtn, background: '#22c55e', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save all'}
        </button>
        <button onClick={load} disabled={loading} style={ghostBtn}>
          {loading ? 'Reloading…' : 'Reload'}
        </button>
        {savedAt && !saving && (
          <span style={{ alignSelf: 'center', fontSize: 11, color: '#22c55e', fontWeight: 600 }}>
            ✓ saved
          </span>
        )}
      </div>

      {/* Rules table */}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
      ) : rules.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13, border: '1px dashed var(--line)', borderRadius: 10 }}>
          No chaos rules yet. Add one above or pick a preset.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rules.map((r) => (
            <RuleRow
              key={r.id}
              rule={r}
              onUpdate={(patch) => updateRule(r.id, patch)}
              onUpdateMatch={(patch) => updateMatch(r.id, patch)}
              onUpdateMock={(patch) => updateMock(r.id, patch)}
              onDelete={() => deleteRule(r.id)}
            />
          ))}
        </div>
      )}

      <p style={{ marginTop: 24, fontSize: 11, color: 'var(--muted)', lineHeight: 1.6 }}>
        Rules are evaluated in order for every intercepted request; the first enabled rule whose
        match criteria all hit applies. Leave a match field blank to skip it.
      </p>

      <datalist id="chaos-request-types">
        {COMMON_REQUEST_TYPES.map((t) => <option key={t} value={t} />)}
      </datalist>
    </div>
  );
}

function RuleRow({ rule, onUpdate, onUpdateMatch, onUpdateMock, onDelete }) {
  const match = rule.match || {};
  const mock = rule.mock || {};
  return (
    <div style={{
      padding: '12px 14px', borderRadius: 10, background: 'var(--surface)',
      border: `1px solid ${rule.enabled ? '#ef444466' : 'var(--line)'}`,
      opacity: rule.enabled ? 1 : 0.75,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!rule.enabled}
            onChange={(e) => onUpdate({ enabled: e.target.checked })}
          />
          Enabled
        </label>

        <Field label="Request type">
          <input
            list="chaos-request-types"
            value={match.requestType || ''}
            onChange={(e) => onUpdateMatch({ requestType: e.target.value })}
            placeholder="any"
            style={{ ...inputStyle, width: 120 }}
          />
        </Field>

        <Field label="Host contains">
          <input
            value={match.host || ''}
            onChange={(e) => onUpdateMatch({ host: e.target.value })}
            placeholder="any"
            style={{ ...inputStyle, width: 160 }}
          />
        </Field>

        <Field label="URL contains">
          <input
            value={match.urlContains || ''}
            onChange={(e) => onUpdateMatch({ urlContains: e.target.value })}
            placeholder="any"
            style={{ ...inputStyle, width: 160 }}
          />
        </Field>

        <Field label="Action">
          <select
            value={rule.action}
            onChange={(e) => onUpdate({ action: e.target.value })}
            style={{ ...inputStyle, width: 130 }}
          >
            {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </Field>

        {rule.action === 'delay' && (
          <Field label="Delay (ms)">
            <input
              type="number"
              min={0}
              value={rule.delayMs ?? 2000}
              onChange={(e) => onUpdate({ delayMs: Number(e.target.value) || 0 })}
              style={{ ...inputStyle, width: 100 }}
            />
          </Field>
        )}

        {rule.action === 'mock' && (
          <>
            <Field label="Status">
              <input
                type="number"
                value={mock.status ?? 200}
                onChange={(e) => onUpdateMock({ status: Number(e.target.value) || 200 })}
                style={{ ...inputStyle, width: 80 }}
              />
            </Field>
            <Field label="Content-Type">
              <input
                value={mock.contentType || ''}
                onChange={(e) => onUpdateMock({ contentType: e.target.value })}
                placeholder="application/json"
                style={{ ...inputStyle, width: 170 }}
              />
            </Field>
            <Field label="Body">
              <input
                value={mock.body || ''}
                onChange={(e) => onUpdateMock({ body: e.target.value })}
                placeholder="{}"
                style={{ ...inputStyle, width: 200, fontFamily: 'monospace' }}
              />
            </Field>
          </>
        )}

        {rule.action === 'patch' && (
          <div style={{ flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Field label="Fields to change (one per line: path = value)" grow>
              <textarea
                value={rule.patch || ''}
                onChange={(e) => onUpdate({ patch: e.target.value })}
                placeholder={'placements.*.feature_config.adnw_imps_second_channel_enabled = false\nplacements.0.feature_config.unified_logging_event_limit = 30'}
                rows={4}
                style={{ ...inputStyle, width: '100%', fontFamily: 'monospace', resize: 'vertical' }}
              />
            </Field>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
              The request still goes to the server; only these JSON fields change before the response reaches the device.
              Use <code>.</code> between keys, a number for an array item, <code>*</code> for every item. A value keeps the
              field's type (a string field stays a string). Lines starting with # are ignored.
            </div>
          </div>
        )}

        {rule.action === 'swap' && (
          <div style={{ flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Field label="IDs to swap (one per line: production_id = test_id)" grow>
              <textarea
                value={rule.swap || ''}
                onChange={(e) => onUpdate({ swap: e.target.value })}
                placeholder={'114351662587_10167188220642588 = 999999999999_111111111111111\n5f3a9c0e1b2d4a0012345678 = 6a1b2c3d4e5f600012345678'}
                rows={4}
                style={{ ...inputStyle, width: '100%', fontFamily: 'monospace', resize: 'vertical' }}
              />
            </Field>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
              The request still goes to the server; every copy of each production ID in the response (including escaped
              copies inside adapter settings) is replaced before it reaches the device. An ID matches only as a whole value:
              Meta placement IDs contain the app ID, so list each placement pair. Host <code>doubleclick.net</code> covers
              Publisher Settings (adapter start-up) and the GAM ad responses. SDK-bidding lines are auctioned on Ad Manager's
              servers against the production placement, so a swapped placement may fail to load the bid; waterfall lines use
              the swapped ID directly. Not available for USB/logcat capture (no response passes through the proxy).
            </div>
          </div>
        )}

        <Field label="Note" grow>
          <input
            value={rule.note || ''}
            onChange={(e) => onUpdate({ note: e.target.value })}
            placeholder="optional"
            style={inputStyle}
          />
        </Field>

        <button onClick={onDelete} style={{ ...miniBtn, color: '#ef4444' }}>Delete</button>
      </div>
    </div>
  );
}

function Field({ label, grow, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: grow ? 1 : undefined, minWidth: grow ? 140 : undefined }}>
      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)' }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  boxSizing: 'border-box', padding: '6px 8px', fontSize: 12,
  borderRadius: 6, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)',
};
const primaryBtn = {
  padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none',
  background: '#3b82f6', color: '#fff', cursor: 'pointer',
};
const ghostBtn = {
  padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8,
  border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink)', cursor: 'pointer',
};
const miniBtn = {
  padding: '5px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6,
  border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink)', cursor: 'pointer', flexShrink: 0,
};
